import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import ShoppingCartCheckoutRounded from '@mui/icons-material/ShoppingCartCheckoutRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useAuth } from '../../../auth/AuthProvider';
import { useNotify } from '../../../lib/notify';
import { posConfigKey } from '../../pos/navFlags';
import { usePaymentMethods, useSellableLocations } from '../../sales/lib/api';
import { useSaveSettings, type ShopSettings } from '../api';
import { FieldGrid, SaveBar, SectionCard, SettingSwitch } from '../ui';

// No cash method chosen = the shop's first active Cash method (the backend falls back to it).
export const posSettingsSchema = z.object({
  posEnabled: z.boolean(),
  posDefaultLocationId: z.string(),
  posCashPaymentMethodId: z.string(),
  posAllowedPaymentMethodIds: z.array(z.string()),
  posReceiptHeader: z.string().max(500, 'At most 500 characters'),
  posReceiptFooter: z.string().max(500, 'At most 500 characters'),
});
export type PosValues = z.infer<typeof posSettingsSchema>;

export function toPosValues(s: ShopSettings): PosValues {
  return {
    posEnabled: s.posEnabled,
    posDefaultLocationId: s.posDefaultLocationId ?? '',
    posCashPaymentMethodId: s.posCashPaymentMethodId ?? '',
    posAllowedPaymentMethodIds: s.posAllowedPaymentMethodIds,
    posReceiptHeader: s.posReceiptHeader ?? '',
    posReceiptFooter: s.posReceiptFooter ?? '',
  };
}

/** The settings patch: empty strings become null (the server's defaults). */
export function toPosPatch(v: PosValues) {
  return {
    posEnabled: v.posEnabled,
    posDefaultLocationId: v.posDefaultLocationId || null,
    posCashPaymentMethodId: v.posCashPaymentMethodId || null,
    posAllowedPaymentMethodIds: v.posAllowedPaymentMethodIds,
    posReceiptHeader: v.posReceiptHeader.trim() || null,
    posReceiptFooter: v.posReceiptFooter.trim() || null,
  };
}

/** Settings → Point of Sale: the per-shop switch and register defaults (pos.manage). */
export function PosTab({ settings, canManage }: { settings: ShopSettings; canManage: boolean }) {
  const { can } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const save = useSaveSettings();
  const locations = useSellableLocations();
  const methods = usePaymentMethods();
  const ro = !canManage || !can('pos.manage');
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<PosValues>({
    values: toPosValues(settings),
    resolver: zodResolver(posSettingsSchema),
  });
  const enabled = watch('posEnabled');
  const cashMethods = (methods.data ?? []).filter((m) => m.kind === 'CASH');

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync(toPosPatch(v));
      await qc.invalidateQueries({ queryKey: posConfigKey });
      notify.success(v.posEnabled ? 'Point of sale saved' : 'Point of sale switched off');
    } catch (e) {
      notify.error(e);
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack spacing={2.5}>
        {!can('pos.manage') && canManage && (
          <Alert severity="info">
            Changing point-of-sale settings needs the “Manage the point of sale” permission.
          </Alert>
        )}
        <SectionCard
          icon={<ShoppingCartCheckoutRounded />}
          title="Point of Sale"
          description="A full-screen register for walk-in sales: product grid, numpad, split payments and cash control."
          action={
            settings.posEnabled ? (
              <Button variant="outlined" onClick={() => void navigate('/pos')}>
                Open register
              </Button>
            ) : undefined
          }
        >
          <Controller
            control={control}
            name="posEnabled"
            render={({ field }) => (
              <SettingSwitch
                title="Use the point of sale"
                description={
                  field.value
                    ? 'The register shows in the menu for staff who may sell at the POS.'
                    : 'Off: staff keep using Fast sale and invoices; the POS stays hidden.'
                }
                checked={field.value}
                onChange={field.onChange}
                disabled={ro}
              />
            )}
          />
        </SectionCard>

        <SectionCard
          icon={<PaymentsRounded />}
          title="Register defaults"
          description="Where POS sales take stock from and how customers can pay."
        >
          <FieldGrid>
            <Controller
              control={control}
              name="posDefaultLocationId"
              render={({ field }) => (
                <TextField
                  select
                  label="Stock location"
                  {...field}
                  disabled={ro}
                  helperText="Default: the shop's default location"
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">Default location</MenuItem>
                  {(locations.data ?? []).map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="posCashPaymentMethodId"
              render={({ field }) => (
                <TextField
                  select
                  label="Cash method (counted at closing)"
                  {...field}
                  disabled={ro}
                  error={!!errors.posCashPaymentMethodId}
                  helperText={errors.posCashPaymentMethodId?.message ?? 'The drawer’s cash'}
                  slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                >
                  <MenuItem value="">Default cash method</MenuItem>
                  {cashMethods.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FieldGrid>
          <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
            Payment methods offered
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            None ticked = every active method. The cash method is always offered.
          </Typography>
          <Controller
            control={control}
            name="posAllowedPaymentMethodIds"
            render={({ field }) => (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr' },
                  gap: 0.5,
                }}
              >
                {(methods.data ?? []).map((m) => (
                  <FormControlLabel
                    key={m.id}
                    disabled={ro}
                    control={
                      <Checkbox
                        checked={field.value.includes(m.id)}
                        onChange={(_, on) =>
                          field.onChange(
                            on ? [...field.value, m.id] : field.value.filter((id) => id !== m.id),
                          )
                        }
                      />
                    }
                    label={m.name}
                  />
                ))}
              </Box>
            )}
          />
        </SectionCard>

        <SectionCard
          icon={<ReceiptRounded />}
          title="Receipt"
          description="Printed on every 80 mm POS receipt."
        >
          <FieldGrid>
            <Controller
              control={control}
              name="posReceiptHeader"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Header"
                  multiline
                  minRows={2}
                  disabled={ro}
                  placeholder="e.g. Free Wi-Fi: cafe-guest"
                  error={!!errors.posReceiptHeader}
                  helperText={errors.posReceiptHeader?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="posReceiptFooter"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Footer"
                  multiline
                  minRows={2}
                  disabled={ro}
                  placeholder="e.g. Thank you — see you again!"
                  error={!!errors.posReceiptFooter}
                  helperText={errors.posReceiptFooter?.message}
                />
              )}
            />
          </FieldGrid>
          {!enabled && settings.posEnabled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Switching the POS off blocks open registers from selling until it is switched on
              again.
            </Alert>
          )}
        </SectionCard>
      </Stack>
      <SaveBar
        dirty={isDirty}
        saving={save.isPending}
        disabled={ro}
        onReset={() => reset(toPosValues(settings))}
      />
    </form>
  );
}
